/**
 * Consecutive-`agentCard` fold — the pure grouping seam behind the chat's
 * multi-agent "agent joined your team" orbit card.
 *
 * The harness persists ONE assistant message per hire, each carrying the
 * `metadata.agentCard` marker. The chat consolidates a RUN of consecutive
 * agentCard-carrying messages into ONE multi-agent `AgentJoinCard`,
 * rendered at the FIRST message of the run (keyed by that message's id, so a
 * new hire appended to the run extends the SAME card rather than remounting
 * it); the rest of the run renders nothing. Any non-card message breaks the
 * run. This mirrors the native apps' fold 1:1 — iOS `ChatScreen.agentCardGroup`
 * (pinned by `AgentCardGroupingTests`) and Android's equivalent — so a burst
 * of hires never shows a cascade of cards on any platform.
 */

export type AgentCardCarrier<C> = { agentCard?: C };

/**
 * Collect the run of consecutive `agentCard`-carrying messages starting at
 * `from` (chronological order). A non-card message breaks the run. Returns the
 * cards in message order; empty when `messages[from]` carries no card.
 *
 * Mirrors iOS `ChatScreen.agentCardGroup(in:from:)`.
 *
 * @example
 * // testCollectsFullRunOfConsecutiveCards:
 * // a run of N consecutive agentCard messages collects all N.
 * const card = (name: string) => ({ agentCard: { name } });
 * const messages = [
 *   {},                                        // user: "add a team"
 *   card('Tempo'), card('Cadence'), card('Rhythm'),
 *   {},                                        // assistant: "done"
 * ];
 * agentCardGroup(messages, 1).map((c) => c.name); // => ['Tempo', 'Cadence', 'Rhythm']
 *
 * @example
 * // testNonCardMessageBreaksTheRun:
 * // starting at the first card, the run is just Tempo — the plain reply
 * // breaks it before Cadence; starting past the break collects the later run.
 * const messages = [card('Tempo'), {}, card('Cadence')];
 * agentCardGroup(messages, 0).map((c) => c.name); // => ['Tempo']
 * agentCardGroup(messages, 2).map((c) => c.name); // => ['Cadence']
 *
 * @example
 * // testSingleCardRunCollectsOne:
 * const messages = [{}, card('Tempo')];
 * agentCardGroup(messages, 1).map((c) => c.name); // => ['Tempo']
 *
 * @example
 * // Not a card at `from` → empty run (the render path never asks in this
 * // case, but the function is total).
 * agentCardGroup([{}, card('Tempo')], 0); // => []
 */
export function agentCardGroup<C>(messages: readonly AgentCardCarrier<C>[], from: number): C[] {
  const out: C[] = [];
  for (let i = Math.max(from, 0); i < messages.length; i++) {
    const card = messages[i].agentCard;
    if (!card) break;
    out.push(card);
  }
  return out;
}

/**
 * True when `messages[index]` carries an `agentCard` AND is the FIRST message
 * of its run (chronologically first card, or the previous message carries no
 * card). The render path draws the consolidated card here and skips the rest
 * of the run — mirroring iOS's `isFirst` check
 * (`chronoIdx == 0 || displayMessages[chronoIdx - 1].agentCard == nil`).
 *
 * @example
 * const card = (name: string) => ({ agentCard: { name } });
 * const messages = [{}, card('Tempo'), card('Cadence'), {}, card('Rhythm')];
 * isAgentCardRunStart(messages, 0); // => false (no card)
 * isAgentCardRunStart(messages, 1); // => true  (run head)
 * isAgentCardRunStart(messages, 2); // => false (folded into Tempo's run)
 * isAgentCardRunStart(messages, 4); // => true  (the break at 3 starts a new run)
 *
 * @example
 * // A card at index 0 is always a run start.
 * isAgentCardRunStart([card('Tempo')], 0); // => true
 */
export function isAgentCardRunStart<C>(messages: readonly AgentCardCarrier<C>[], index: number): boolean {
  return !!messages[index]?.agentCard && (index === 0 || !messages[index - 1]?.agentCard);
}

export type AgentIdentity = { id?: string; name: string };

/**
 * The team as it stood BEFORE this join: the full roster minus everyone in the
 * run that is joining. The card draws these underneath the new hire(s) so the
 * moment reads as "here's the team, and here's who just joined" rather than a
 * lone node on an empty ring.
 *
 * Matched on the stable id OR the name, because a card marker persisted before
 * ids were threaded through carries only a name — and an agent drawn twice on
 * one ring is the defect this exists to prevent.
 *
 * Mirrors iOS `ChatMessageList.existingAgents(fullRoster:joining:)` and the
 * Android call site.
 *
 * @example
 * const roster = [{ id: 'a', name: 'Tempo' }, { id: 'b', name: 'Cadence' }];
 * existingAgents(roster, [{ id: 'b', name: 'Cadence' }]); // => [{ id: 'a', name: 'Tempo' }]
 * existingAgents(roster, [{ name: 'Tempo' }]);            // => [{ id: 'b', name: 'Cadence' }]
 */
export function existingAgents<A extends AgentIdentity>(
  fullRoster: readonly A[],
  joining: readonly AgentIdentity[],
): A[] {
  const joiningIds = new Set(joining.map((a) => a.id).filter(Boolean));
  const joiningNames = new Set(joining.map((a) => a.name));
  return fullRoster.filter((agent) => !(agent.id && joiningIds.has(agent.id)) && !joiningNames.has(agent.name));
}
